import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { firebaseObject } from '../config/firebase';
import { withRetry } from '../utils/retryHelper';
import { quotaBus } from './quotaBus';

export class ApiService {
  private static instance: ApiService;
  private axiosInstance: AxiosInstance;
  private reconnectAttempts: number = 0;
  private readonly MAX_RECONNECT_ATTEMPTS = 5;

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_API_URL || '',
      timeout: 30000, // 30 seconds timeout
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        const token = await this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // If impersonating, attach the impersonated user's UID as a header
        try {
          const impersonationRaw = localStorage.getItem('impersonation_state');
          if (impersonationRaw) {
            const impersonationState = JSON.parse(impersonationRaw);
            if (impersonationState?.isImpersonating && impersonationState?.impersonatedUser?.uid) {
              config.headers['X-Impersonate-UID'] = impersonationState.impersonatedUser.uid;
            }
          }
        } catch (_) { /* ignore */ }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Reset reconnect attempts on successful response
        this.reconnectAttempts = 0;
        return response;
      },
      async (error) => {
        // Handle network errors with exponential backoff
        if (!error.response && this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
          this.reconnectAttempts++;
          const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 10000);
          console.log(`Network error, retrying in ${delay}ms (attempt ${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.axiosInstance.request(error.config);
        }

        if (error.response?.status === 401) {
          // Token might be expired, try to refresh
          const user = firebaseObject.auth.currentUser;
          if (user) {
            try {
              await user.getIdToken(true); // Force refresh
              // Retry the original request
              const token = await this.getAuthToken();
              if (token && error.config) {
                error.config.headers.Authorization = `Bearer ${token}`;
                return this.axiosInstance.request(error.config);
              }
            } catch (refreshError) {
              console.error('Failed to refresh token:', refreshError);
              // Optionally redirect to login or handle auth failure
            }
          }
        }
        
        // Reset reconnect attempts on other errors
        if (error.response) {
          this.reconnectAttempts = 0;
        }

        // Plan quota rejections (monthly usage limits) — surface globally so
        // the quota banner / composer state react wherever the 429 happened.
        try {
          const detail = error.response?.data?.detail;
          if (error.response?.status === 429 && detail?.error === 'quota_exceeded') {
            quotaBus.publish(detail);
          }
        } catch { /* never break error propagation */ }

        return Promise.reject(error);
      }
    );
  }

  private async getAuthToken(): Promise<string | null> {
    const user = firebaseObject.auth.currentUser;
    if (user) {
      try {
        return await user.getIdToken();
      } catch (error) {
        console.error('Failed to get auth token:', error);
      }
    }
    return null;
  }

  // HTTP methods with automatic auth and retry logic
  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return withRetry(
      () => this.axiosInstance.get<T>(url, config),
      {
        maxRetries: 2,
        onRetry: (attempt) => console.log(`Retrying GET ${url}, attempt ${attempt}`),
      }
    );
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return withRetry(
      () => this.axiosInstance.post<T>(url, data, config),
      {
        maxRetries: 2,
        shouldRetry: (error) => {
          // Don't retry POST on some errors to avoid duplicates
          if (error.response?.status === 409 || error.response?.status === 400) return false;
          return !error.response || error.response.status >= 500;
        },
        onRetry: (attempt) => console.log(`Retrying POST ${url}, attempt ${attempt}`),
      }
    );
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return withRetry(
      () => this.axiosInstance.put<T>(url, data, config),
      {
        maxRetries: 2,
        onRetry: (attempt) => console.log(`Retrying PUT ${url}, attempt ${attempt}`),
      }
    );
  }

  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return withRetry(
      () => this.axiosInstance.patch<T>(url, data, config),
      {
        maxRetries: 2,
        onRetry: (attempt) => console.log(`Retrying PATCH ${url}, attempt ${attempt}`),
      }
    );
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return withRetry(
      () => this.axiosInstance.delete<T>(url, config),
      {
        maxRetries: 2,
        onRetry: (attempt) => console.log(`Retrying DELETE ${url}, attempt ${attempt}`),
      }
    );
  }

  // For fetch API calls (like SSE), create authenticated headers
  public async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Include impersonation header for SSE/fetch calls too
    try {
      const impersonationRaw = localStorage.getItem('impersonation_state');
      if (impersonationRaw) {
        const impersonationState = JSON.parse(impersonationRaw);
        if (impersonationState?.isImpersonating && impersonationState?.impersonatedUser?.uid) {
          headers['X-Impersonate-UID'] = impersonationState.impersonatedUser.uid;
        }
      }
    } catch (_) { /* ignore */ }
    
    return headers;
  }

  // Authenticated fetch wrapper
  public async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const headers = await this.getAuthHeaders();
    
    const config: RequestInit = {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    };

    const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL || ''}${url}`;
    return fetch(fullUrl, config);
  }

  // Settings API methods
  public async getUserSettings(): Promise<AxiosResponse<any>> {
    return this.get('/api/user/settings/settings');
  }

  public async updateUserSettings(settings: any): Promise<AxiosResponse<any>> {
    return this.post('/api/user/settings/settings', settings);
  }

  public async setUserLanguage(language: string): Promise<AxiosResponse<any>> {
    return this.post('/api/user/language', { language });
  }

  // User Profile API methods
  public async getUserProfile(): Promise<AxiosResponse<any>> {
    return this.get('/api/user/profile');
  }

  public async updateUserProfile(profileData: {
    name?: string;
    middle_name?: string;
    last_name?: string;
    email?: string;
    picture?: string;
  }): Promise<AxiosResponse<any>> {
    return this.put('/api/user/profile', profileData);
  }

  // File upload methods
  public async uploadPricingFile(catalogName: string, file: File): Promise<AxiosResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('catalog_name', catalogName);
    
    return this.axiosInstance.post('/api/user/settings/upload-pricing', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Test catalog configuration
  public async testCatalogConfig(config: any): Promise<AxiosResponse<any>> {
    // Catalog testing can take longer due to browser automation
    return this.post('/api/user/settings/test-config', config, { timeout: 120000 }); // 120 seconds timeout
  }

  // Orders API methods
  public async createOrder(orderData: any): Promise<AxiosResponse<any>> {
    return this.post('/api/user/orders/', orderData);
  }

  public async getUserOrders(params?: URLSearchParams): Promise<AxiosResponse<any>> {
    const url = params ? `/api/user/orders/?${params.toString()}` : '/api/user/orders/';
    return this.get(url);
  }

  public async getOrderById(orderId: number): Promise<AxiosResponse<any>> {
    return this.get(`/api/user/orders/${orderId}`);
  }

  public async updateOrder(orderId: number, updateData: any): Promise<AxiosResponse<any>> {
    return this.put(`/api/user/orders/${orderId}`, updateData);
  }

  public async updateOrderStatus(orderId: number, status: string): Promise<AxiosResponse<any>> {
    return this.patch(`/api/user/orders/${orderId}/status`, null, {
      params: { status }
    });
  }

  public async deleteOrder(orderId: number): Promise<AxiosResponse<any>> {
    return this.delete(`/api/user/orders/${orderId}`);
  }

  // Organization API methods
  public async getOrganizationInfo(): Promise<AxiosResponse<any>> {
    return this.get('/api/organization/info');
  }

  public async createOrganization(data: { name: string; description?: string }): Promise<AxiosResponse<any>> {
    return this.post('/api/organization/create', data);
  }

  public async getOrganizationSettings(): Promise<AxiosResponse<any>> {
    return this.get('/api/organization/settings');
  }

  public async updateOrganizationSettings(settings: any): Promise<AxiosResponse<any>> {
    return this.post('/api/organization/settings', settings);
  }

  public async getOrganizationMembers(): Promise<AxiosResponse<any>> {
    return this.get('/api/organization/members');
  }

  public async addOrganizationMember(data: { user_email: string; role: string }): Promise<AxiosResponse<any>> {
    return this.post('/api/organization/members', data);
  }

  public async removeOrganizationMember(userUid: string): Promise<AxiosResponse<any>> {
    return this.delete(`/api/organization/members/${userUid}`);
  }

  public async toggleUseOrganizationSettings(useOrgSettings: boolean): Promise<AxiosResponse<any>> {
    return this.post('/api/user/settings/toggle-org-settings', { use_org_settings: useOrgSettings });
  }
}

// Export singleton instance
export const apiService = ApiService.getInstance();
